import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import NavigationButtons from "@/components/elements/Buttons";
import Label from "@/components/elements/Label";
import Dropdown from "@/components/elements/Dropdown";
import Drawing from "@/components/drawing/drawing";
import api from "@/services/api";

/**
 * Finishing (Finishing) Configuration Component
 *
 * Handles the configuration of product finishing options including groove/edge finishing
 * settings. Allows users to specify whether they want finishing, whether all sides
 * should have equal finishing, and additional spring attachment options.
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.step - Current configuration step identifier
 * @param {Array} props.steps - Available configuration steps
 * @param {Object} props.product - Product configuration data
 * @param {Object} props.product.properties - Current product properties
 * @param {string} props.heading - Page heading text
 * @param {string} props.subheading - Page subheading text
 * @param {Array} props.questions - Configuration questions (unused in this component)
 * @param {string} props.saveUrl - API endpoint for saving configuration
 * @param {string} props.prevLink - URL for previous step navigation
 * @param {string} props.nextLinkIngave - URL for next step when no finishing
 * @param {string} props.nextLinkSame - URL for next step when equal finishing on all sides
 * @param {string} props.nextLinkLeft - URL for next step when individual side finishing
 * @param {Array} props.colors - Available color options for visualization
 *
 * @returns {JSX.Element} The rendered finishing configuration page
 */
export default function Finishing({
    session,
    shoppingurl,
    step,
    steps,
    product,
    heading,
    subheading,
    questions,
    saveUrl,
    prevLink,
    nextLinkIngave,
    nextLinkSame,
    nextLinkLeft,
    colors,
}) {
    // Initialize state with current product data
    const [productprops, setProductprops] = useState(product);
    const [properties, setProperties] = useState(product.properties);
    const [stepList, setStepList] = useState(steps);

    /**
     * Update available navigation steps based on current configuration
     * Fetches updated step list from server after configuration changes
     */
    function updateSteps() {
        api.get(
            `/api/steps/${product.session_id}/${product.product_id}/${step}`,
        ).then((response) => {
            setStepList(response.data);
        });
    }

    /**
     * Update spring attachment setting
     * Handles the VEER (spring) property update with immediate server save
     *
     * @param {boolean} veer - Whether spring should be attached
     */
    function updateVeer(veer) {
        properties["VEER"] = veer;
        api.put(saveUrl, { properties });
    }

    /**
     * Handle basic finishing option changes
     * Updates properties and saves to server without additional logic
     *
     * @param {Object} value - Updated properties object
     * @param {string} property - Property name being updated
     */
    function updateFinish(value, property) {
        let props = { ...value };

        setProperties(props);

        api.put(saveUrl, { properties: props });
    }

    /**
     * Handle "all sides equal finishing" toggle with complex side effect logic
     * When enabled, applies the same finishing to all sides and sets default values
     * When disabled, resets to default inner finishing
     *
     * @param {Object} value - Updated properties object
     * @param {string} property - Property being updated (randen_afwerking_gelijk)
     */
    function updateAllEqual(value, property) {
        let props = { ...value };
        let sponning = ["boven", "onder", "links", "rechts"]; // All sides

        let sponning_side = props.sponning_rondom;

        // Apply same finishing to all sides if equal finishing is enabled
        if (props[property] === 1) {
            sponning.map((item) => {
                props["sponning_" + item] = sponning_side;
            });
        }

        // Set default values for all groove dimensions
        let breedte = ["BO_BR", "LI_BR", "RE_BR", "O_BR"]; // Width properties
        let hoogte = ["BO_DI", "LI_DI", "RE_DI", "O_DI"];   // Height properties
        let sponningvalue = props[property] === 1 ? 1 : 0;

        // Apply groove width settings
        breedte.map((item) => {
            props[sponning_side + "_" + item] = sponningvalue;
        });

        // Apply groove height settings
        hoogte.map((item) => {
            props[sponning_side + "_" + item] = sponningvalue;
        });

        // Set overall groove dimensions
        props["sponning_rondom_breedte"] = sponningvalue;
        props["sponning_rondom_hoogte"] = sponningvalue;

        // Default to inner finishing when disabling equal finishing
        if (value === 0) {
            props.sponning_rondom = "SP_BI";
        }

        setProperties(props);

        // Save to server and update visualization
        api.put(saveUrl, { properties: props }).then((response) => {
            setProductprops(response.data);
            updateSteps();
        });
    }

    /**
     * Determine the next navigation link based on finishing configuration
     * Routes to different steps based on user's finishing choices
     *
     * @returns {string} URL for the next configuration step
     */
    const getNextLink = () => {
        if (properties.toon_afwerking) {
            if (properties.randen_afwerking_gelijk) {
                return nextLinkSame;  // All sides equal finishing
            }
            return nextLinkLeft;      // Individual side finishing
        }
        return nextLinkIngave;        // No finishing, go to input step
    };

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
                step={step}
            />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
            >
                <ContentOptions>
                    <Heading heading={heading} subheading={subheading} />

                    <div className="question">
                        <div className="d-block">
                            <Label label="Sponning gewenst?" />
                            <Dropdown
                                properties={properties}
                                property={"toon_afwerking"}
                                updateProperties={updateFinish}
                                boolean
                                numbervalue
                                options={[
                                    { name: "Ja", value: 1 },
                                    { name: "Nee", value: 0 },
                                ]}
                            />
                        </div>
                        {properties["toon_afwerking"] ? (
                            <div className="d-block">
                                <Label label="Alle randen rondom gelijke sponning/randafwerking?" />
                                <Dropdown
                                    properties={properties}
                                    property={"randen_afwerking_gelijk"}
                                    updateProperties={updateAllEqual}
                                    boolean
                                    numbervalue
                                    options={[
                                        { name: "Ja", value: 1 },
                                        { name: "Nee", value: 0 },
                                    ]}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {properties["toon_afwerking"] ? (
                            <div className="d-block">
                                <input
                                    id="veer_toevoegen"
                                    name="veer_toevoegen"
                                    className="ml-1 mr-2"
                                    type="checkbox"
                                    defaultChecked={properties["VEER"]}
                                    onChange={(event) =>
                                        updateVeer(event.target.checked)
                                    }
                                />
                                <Label label="Veer toevoegen?" labelfor="veer_toevoegen" />
                            </div>
                        ) : (
                            <></>
                        )}
	                    <div className="d-block">
		                    <Label label="Tapen?" labelfor="TAPE" />
                            <Dropdown
                                properties={properties}
                                property={"TAPE"}
                                updateProperties={updateFinish}
                                options={[
                                    { name: "Nee", value: 'none' },
                                    { name: "Rondom", value: 'all' },
                                    { name: "Per zijde", value: 'custom' },
                                ]}
                            />
	                    </div>
                        {properties["TAPE"] === 'custom' ? (
                            <>
                                <div className="d-block">
                                    <input
                                        id="tape_top"
                                        name="tape_top"
                                        className="ml-1 mr-2"
                                        type="checkbox"
                                        defaultChecked={properties["tape_top"]}
                                        onChange={(event) =>
                                            updateFinish({...properties, tape_top: event.target.checked})
                                        }
                                    />
                                    <Label label="Boven" labelfor="tape_top" />
                                </div>
                                <div className="d-block">
                                    <input
                                        id="tape_right"
                                        name="tape_right"
                                        className="ml-1 mr-2"
                                        type="checkbox"
                                        defaultChecked={properties["tape_right"]}
                                        onChange={(event) =>
                                            updateFinish({...properties, tape_right: event.target.checked})
                                        }
                                    />
                                    <Label label="Rechts" labelfor="tape_right" />
                                </div>
                                <div className="d-block">
                                    <input
                                        id="tape_under"
                                        name="tape_under"
                                        className="ml-1 mr-2"
                                        type="checkbox"
                                        defaultChecked={properties["tape_under"]}
                                        onChange={(event) =>
                                            updateFinish({...properties, tape_under: event.target.checked})
                                        }
                                    />
                                    <Label label="Onder" labelfor="tape_under" />
                                </div>
                                <div className="d-block">
                                    <input
                                        id="tape_left"
                                        name="tape_left"
                                        className="ml-1 mr-2"
                                        type="checkbox"
                                        defaultChecked={properties["tape_left"]}
                                        onChange={(event) =>
                                            updateFinish({...properties, tape_left: event.target.checked})
                                        }
                                    />
                                    <Label label="Links" labelfor="tape_left" />
                                </div>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>

                    <NavigationButtons
                        prevlink={prevLink}
                        nextlink={getNextLink()}
                    />
                </ContentOptions>
                <Drawing
                    session_product={productprops}
                    properties={properties}
                    products={product.products}
                    sponningside={properties.sponning_rondom}
                    sidepanel
                    colors={colors}
                    afwerkingactive
                />
            </Content>
        </Layout>
    );
}
